import React, { useState } from 'react';
import { Play, Edit, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

interface ContentCardProps {
  id: string;
  title: string;
  description: string;
  image: string;
  progress: number;
  isCompleted: boolean;
  videoCount: string;
  tags?: string[];
  isAdmin?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
}

const ContentCard: React.FC<ContentCardProps> = ({
  id,
  title,
  description,
  image,
  progress,
  isCompleted,
  videoCount,
  tags,
  isAdmin = false,
  onClick,
  onEdit,
  onDelete,
  onMoveLeft,
  onMoveRight
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div className="relative flex flex-col items-center w-full max-w-[480px] md:max-w-none">
      {/* Admin Actions - Horizontal acima do card */}
      {isAdmin && (
        <div className="flex items-center justify-center gap-2 mb-1 w-full">
          {onMoveLeft && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onMoveLeft();
              }}
              size="sm"
              variant="outline"
              className="w-8 h-8 p-0 bg-[#1a1d23] border-[#1f2329] text-gray-400 hover:text-white"
              title="Mover para esquerda"
            >
              <ArrowLeft className="w-3 h-3" />
            </Button>
          )}
          {onMoveRight && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                onMoveRight();
              }}
              size="sm"
              variant="outline"
              className="w-8 h-8 p-0 bg-[#1a1d23] border-[#1f2329] text-gray-400 hover:text-white"
              title="Mover para direita"
            >
              <ArrowRight className="w-3 h-3" />
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            size="sm"
            variant="outline"
            className="w-8 h-8 p-0 bg-[#1a1d23] border-[#1f2329] text-gray-400 hover:text-white"
          >
            <Edit className="w-3 h-3" />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
            size="sm"
            variant="outline"
            className="w-8 h-8 p-0 bg-[#1a1d23] border-red-600 text-red-400 hover:text-red-300"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Main Card - Proporção 16:9, tamanho máximo */}
      <div
        onClick={onClick}
        className="relative group cursor-pointer overflow-hidden bg-[#121316] rounded-2xl border-2 border-[#0FFF55] hover:border-[#0dd149] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#0FFF55]/30 w-full"
        style={{ aspectRatio: '16/9' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Background Image */}
        <div className="relative w-full h-full">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
          <div className="hidden w-full h-full flex items-center justify-center text-gray-500 text-sm bg-[#0f1114]">
            Imagem não encontrada
          </div>
        </div>

        {/* Overlay Gradient - Suave na parte inferior */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />

        {/* Bottom Section - Progress and Video Count */}
        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#0FFF55] rounded-full flex items-center justify-center group-hover:bg-[#0dd149] transition-colors">
              <Play className="w-4 h-4 text-black fill-current" />
            </div>
            <span className="text-white text-sm font-medium">{videoCount}</span>
          </div>
          <div className="flex-1 ml-4 flex flex-col items-end gap-1">
            <div className="w-full bg-gray-700/80 rounded-full h-2">
              <div 
                className="bg-[#0FFF55] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-white text-[10px] font-bold tracking-wider">
              {progress}% Concluído
            </span>
          </div>
        </div>

        {/* Completion Badge */}
        {isCompleted && (
          <div className="absolute top-3 right-3">
            <div className="bg-[#0FFF55] text-black text-xs font-bold px-2 py-1 rounded-full shadow">
              ✓ Concluído
            </div>
          </div>
        )}
      </div>

      {/* Título movido para FORA da thumbnail, posicionado ABAIXO do card */}
      <div className="w-full mt-3 px-1 text-left">
        <h3 
          onClick={onClick}
          className="text-white font-bold text-base md:text-lg leading-snug line-clamp-2 hover:text-[#0FFF55] transition-colors cursor-pointer"
        >
          {title}
        </h3>
      </div>

      {/* Descrição sobrepõe card inferior - apenas se houver descrição cadastrada */}
      {description && description.trim().length > 0 && (
        <div
          className={`w-full transition-all duration-300 ${hovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}
          style={{ 
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            marginTop: '-20px'
          }}
        >
          <div className="bg-black/95 backdrop-blur-sm rounded-lg p-3 border border-[#0FFF55]/20 shadow-xl">
            <p className="text-white text-sm leading-relaxed">
              {description.trim().length > 140
                ? `${description.trim().slice(0, 140)}...`
                : description.trim()}
            </p>
            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.slice(0, 3).map((tag, index) => (
                  <span
                    key={index}
                    className="inline-block bg-[#0FFF55]/20 text-[#0FFF55] text-xs px-2 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCard;